import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { accidentsAPI, vehiclesAPI, trailersAPI, driversAPI } from '../services/api';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  AlertOctagon, 
  Plus, 
  Loader2, 
  Calendar,
  Truck,
  CheckCircle2,
  Clock,
  ChevronRight,
  Edit,
  Trash2,
  Eye,
  Send,
  Check,
  X,
  Upload,
  Download,
  FileText,
  User,
  CloudRain,
  Sun,
  Moon,
  Skull,
  AlertTriangle,
  XCircle,
  File,
  ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SUPPORTING_DOC_TYPES = {
  driver_statement: 'Driver Statement',
  maintenance_history: 'Maintenance History',
  last_service: 'Last Service',
  last_inspection_sheet: 'Last Inspection Sheet',
  workshop_findings: 'Workshop Findings',
  police_report: 'Police Report',
  pictures: 'Pictures',
  sketch_of_accident: 'Sketch of Accident',
  witness_statements: 'Witness Statements',
  external_reports: 'External Reports',
  polygraph_results: 'Polygraph Results',
  alcohol_test_results: 'Alcohol Test Results',
};

const getStatusBadge = (status) => {
  const styles = {
    draft: 'bg-slate-100 text-slate-700',
    submitted: 'bg-blue-100 text-blue-700',
    pending_approval: 'bg-amber-100 text-amber-700',
    approved: 'bg-emerald-100 text-emerald-700',
    returned: 'bg-red-100 text-red-700',
    closed: 'bg-slate-200 text-slate-800',
  };
  return styles[status] || 'bg-slate-100 text-slate-700';
};

const getStatusLabel = (status) => {
  const labels = {
    draft: 'Draft',
    submitted: 'Submitted',
    pending_approval: 'Pending Approval',
    approved: 'Approved',
    returned: 'Returned',
    closed: 'Closed',
  };
  return labels[status] || status;
};

// Supporting Document Upload Component
const SupportingDocUpload = ({ docKey, label, docData, onUpload, onReasonChange, accidentId, disabled }) => {
  const [uploading, setUploading] = useState(false);
  const hasFile = docData?.uploaded;
  const filename = docData?.filename;
  const reason = docData?.reason || '';

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!accidentId) {
      toast.error('Please save the accident first before uploading documents');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('doc_type', docKey);
      
      const uploadResp = await axios.post(
        `${API_URL}/api/accidents/${accidentId}/upload-document`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      const result = uploadResp.data;
      onUpload(docKey, result.filename);
      toast.success(`${label} uploaded successfully`);
    } catch (error) {
      toast.error(`Failed to upload ${label}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="border rounded-lg p-3 space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {hasFile ? (
            <CheckCircle2 className="w-5 h-5 text-emerald-500" />
          ) : (
            <XCircle className="w-5 h-5 text-slate-300" />
          )}
          <span className={`text-sm font-medium ${hasFile ? 'text-emerald-700' : 'text-slate-600'}`}>
            {label}
          </span>
        </div>
        <label>
          <input
            type="file"
            className="hidden"
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            onChange={handleFileUpload}
            disabled={disabled || uploading}
          />
          <Button
            variant={hasFile ? "outline" : "default"}
            size="sm"
            className="gap-1"
            disabled={disabled || uploading}
            asChild
          >
            <span>
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {hasFile ? 'Replace' : 'Upload'}
            </span>
          </Button>
        </label>
      </div>
      
      {hasFile && filename && (
        <div className="flex items-center gap-2 text-sm text-emerald-600 bg-emerald-50 rounded px-2 py-1">
          <File className="w-4 h-4" />
          <span className="truncate flex-1">{filename}</span>
          <a 
            href={`${API_URL}/api/uploads/accidents/${filename}`}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline flex items-center gap-1 text-blue-600"
          >
            <Download className="w-4 h-4" />
          </a>
        </div>
      )}
      
      {!hasFile && (
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">Reason if not uploaded *</Label>
          <Input
            value={reason}
            onChange={(e) => onReasonChange(docKey, e.target.value)}
            placeholder="Provide reason for not uploading this document"
            className="text-sm"
            disabled={disabled}
          />
        </div>
      )}
    </div>
  );
};

// Accident Detail Dialog
const AccidentDetailDialog = ({ open, onClose, accident, onApprove, onReturn, canApprove }) => {
  if (!accident) return null;

  const supportingDocs = accident.supporting_docs || {};

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertOctagon className="w-5 h-5 text-red-500" />
            Accident Report Details
            {accident.is_fatality && (
              <Badge className="bg-black text-white ml-2">FATALITY</Badge>
            )}
          </DialogTitle>
          <div className="flex items-center gap-2 mt-2">
            <Badge className={getStatusBadge(accident.status)}>
              {getStatusLabel(accident.status)}
            </Badge>
            <span className="text-sm text-slate-500">
              {accident.date_of_accident?.slice(0, 10)}
            </span>
          </div>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Basic Info */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-slate-50 rounded-lg">
            <div>
              <Label className="text-xs text-slate-500">Truck</Label>
              <p className="font-mono font-bold">{accident.truck_registration}</p>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Trailer</Label>
              <p className="font-mono">{accident.trailer_registration || 'N/A'}</p>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Driver</Label>
              <p>{accident.driver_name || 'Unknown'}</p>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Date/Time</Label>
              <p>{accident.date_of_accident?.replace('T', ' ')}</p>
            </div>
          </div>

          {/* Fatality/Injury Info */}
          {(accident.is_fatality || accident.injury_count > 0) && (
            <div className={`p-4 rounded-lg ${accident.is_fatality ? 'bg-red-50 border border-red-200' : 'bg-amber-50 border border-amber-200'}`}>
              <h3 className="font-semibold text-slate-900 mb-2">Fatality/Injury Information</h3>
              <div className="grid grid-cols-2 gap-4">
                {accident.is_fatality && (
                  <div>
                    <Label className="text-xs text-red-600">Fatalities</Label>
                    <p className="text-2xl font-bold text-red-700">{accident.fatality_count || 0}</p>
                  </div>
                )}
                <div>
                  <Label className="text-xs text-amber-600">Injuries</Label>
                  <p className="text-2xl font-bold text-amber-700">{accident.injury_count || 0}</p>
                </div>
              </div>
            </div>
          )}

          {/* Supporting Documents */}
          <div className="space-y-3">
            <h3 className="font-semibold text-slate-900 border-b pb-2 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Supporting Documents
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {Object.entries(SUPPORTING_DOC_TYPES).map(([key, label]) => {
                const doc = supportingDocs[key] || {};
                // Handle both old boolean format and new object format
                const isUploaded = typeof doc === 'boolean' ? doc : doc.uploaded;
                const filename = typeof doc === 'object' ? doc.filename : null;
                const reason = typeof doc === 'object' ? doc.reason : null;
                
                return (
                  <div key={key} className="flex items-center justify-between p-3 border rounded">
                    <div className="flex items-center gap-2">
                      {isUploaded ? (
                        <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-400" />
                      )}
                      <span className="text-sm">{label}</span>
                    </div>
                    {isUploaded && filename ? (
                      <a 
                        href={`${API_URL}/api/uploads/accidents/${filename}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                      >
                        <Download className="w-3 h-3" />
                        Download
                      </a>
                    ) : isUploaded ? (
                      <span className="text-xs text-emerald-600">Attached (legacy)</span>
                    ) : (
                      <span className="text-xs text-slate-500 italic max-w-[200px] truncate">
                        {reason || 'Not uploaded'}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Mandatory Documents */}
          <div className="space-y-3">
            <h3 className="font-semibold text-slate-900 border-b pb-2 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              Mandatory Documents
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="flex items-center justify-between p-3 border rounded">
                <div className="flex items-center gap-2">
                  {accident.incident_statement_uploaded ? (
                    <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-400" />
                  )}
                  <span className="text-sm">Incident/Accident Statement</span>
                </div>
                {accident.incident_statement_uploaded && accident.incident_statement_filename ? (
                  <a 
                    href={`${API_URL}/api/uploads/accidents/${accident.incident_statement_filename}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                  >
                    <Download className="w-3 h-3" />
                    Download
                  </a>
                ) : accident.incident_statement_uploaded ? (
                  <span className="text-sm text-emerald-600">Uploaded</span>
                ) : (
                  <span className="text-sm text-red-600">Required</span>
                )}
              </div>
              {accident.is_fatality && (
                <div className="flex items-center justify-between p-3 border rounded">
                  <div className="flex items-center gap-2">
                    {accident.fatality_report_uploaded ? (
                      <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-400" />
                    )}
                    <span className="text-sm">Fatality Investigation Report</span>
                  </div>
                  {accident.fatality_report_uploaded && accident.fatality_report_filename ? (
                    <a 
                      href={`${API_URL}/api/uploads/accidents/${accident.fatality_report_filename}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                    >
                      <Download className="w-3 h-3" />
                      Download
                    </a>
                  ) : accident.fatality_report_uploaded ? (
                    <span className="text-sm text-emerald-600">Uploaded</span>
                  ) : (
                    <span className="text-sm text-red-600">Required</span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Uploaded Documents List */}
          {accident.uploaded_documents && accident.uploaded_documents.length > 0 && (
            <div className="space-y-3">
              <h3 className="font-semibold text-slate-900 border-b pb-2 flex items-center gap-2">
                <File className="w-4 h-4" />
                Uploaded Files ({accident.uploaded_documents.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {accident.uploaded_documents.map((filename, idx) => (
                  <a
                    key={idx}
                    href={`${API_URL}/api/uploads/accidents/${filename}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 p-2 border rounded hover:bg-slate-50 transition-colors"
                  >
                    <File className="w-4 h-4 text-blue-500" />
                    <span className="text-sm text-blue-600 truncate flex-1">{filename}</span>
                    <Download className="w-4 h-4 text-slate-400" />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Conditions */}
          {accident.conditions && (
            <div className="space-y-3">
              <h3 className="font-semibold text-slate-900 border-b pb-2">Conditions of Event</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Day/Night</Label>
                  <p className="capitalize">{accident.conditions.day_night || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Weather</Label>
                  <p className="capitalize">{accident.conditions.weather || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Time</Label>
                  <p>{accident.conditions.time_of_event || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Day of Week</Label>
                  <p>{accident.conditions.day_of_week || 'N/A'}</p>
                </div>
              </div>
            </div>
          )}

          {/* Company Rep & Reporting */}
          <div className="space-y-3">
            <h3 className="font-semibold text-slate-900 border-b pb-2">Company Representative & Reporting</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-3 border rounded">
                <Label className="text-xs text-slate-500">Company rep at scene?</Label>
                <p className={accident.company_rep_present?.answer ? 'text-emerald-600' : 'text-red-600'}>
                  {accident.company_rep_present?.answer ? 'Yes' : 'No'}
                </p>
                {!accident.company_rep_present?.answer && accident.company_rep_present?.reason && (
                  <p className="text-sm text-slate-500 mt-1">Reason: {accident.company_rep_present.reason}</p>
                )}
              </div>
              <div className="p-3 border rounded">
                <Label className="text-xs text-slate-500">Driver reported immediately?</Label>
                <p className={accident.driver_reported_immediately?.answer ? 'text-emerald-600' : 'text-red-600'}>
                  {accident.driver_reported_immediately?.answer ? 'Yes' : 'No'}
                </p>
                {!accident.driver_reported_immediately?.answer && accident.driver_reported_immediately?.reason && (
                  <p className="text-sm text-slate-500 mt-1">Reason: {accident.driver_reported_immediately.reason}</p>
                )}
              </div>
            </div>
          </div>

          {/* Root Cause Analysis */}
          {accident.root_cause_analysis && (
            <div className="space-y-3">
              <h3 className="font-semibold text-slate-900 border-b pb-2">Root Cause Analysis</h3>
              <div className="space-y-4">
                <div>
                  <Label className="text-xs text-slate-500">Problem Description</Label>
                  <p className="text-sm bg-slate-50 p-2 rounded">{accident.root_cause_analysis.problem_description || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">6 WHY's Analysis</Label>
                  <div className="space-y-1 bg-slate-50 p-2 rounded">
                    {[1,2,3,4,5,6].map(num => (
                      accident.root_cause_analysis[`why_${num}`] && (
                        <p key={num} className="text-sm">
                          <span className="font-semibold">WHY {num}:</span> {accident.root_cause_analysis[`why_${num}`]}
                        </p>
                      )
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-slate-500">Root Cause</Label>
                    <p className="text-sm bg-slate-50 p-2 rounded">{accident.root_cause_analysis.root_cause || 'N/A'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Solution</Label>
                    <p className="text-sm bg-slate-50 p-2 rounded">{accident.root_cause_analysis.solution || 'N/A'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Temporary Countermeasure</Label>
                    <p className="text-sm bg-slate-50 p-2 rounded">{accident.root_cause_analysis.temporary_countermeasure || 'N/A'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Permanent Countermeasure</Label>
                    <p className="text-sm bg-slate-50 p-2 rounded">{accident.root_cause_analysis.permanent_countermeasure || 'N/A'}</p>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Monitoring Actions</Label>
                  <p className="text-sm bg-slate-50 p-2 rounded">{accident.root_cause_analysis.monitoring_actions || 'N/A'}</p>
                </div>
              </div>
            </div>
          )}

          {/* Driver Questionnaire */}
          {accident.driver_questionnaire && (
            <div className="space-y-3">
              <h3 className="font-semibold text-slate-900 border-b pb-2">Driver Questionnaire</h3>
              <div className="space-y-3">
                <div className="p-3 border rounded">
                  <Label className="text-xs text-slate-500">Did daily inspection?</Label>
                  <p className={accident.driver_questionnaire.did_daily_inspection ? 'text-emerald-600' : 'text-red-600'}>
                    {accident.driver_questionnaire.did_daily_inspection ? 'Yes' : 'No'}
                  </p>
                  {!accident.driver_questionnaire.did_daily_inspection && accident.driver_questionnaire.daily_inspection_reason_no && (
                    <p className="text-sm text-slate-500 mt-1">Reason: {accident.driver_questionnaire.daily_inspection_reason_no}</p>
                  )}
                </div>
                <div className="p-3 border rounded">
                  <Label className="text-xs text-slate-500">Reported problems before leaving?</Label>
                  <p className={accident.driver_questionnaire.reported_problems_before_leaving ? 'text-emerald-600' : 'text-red-600'}>
                    {accident.driver_questionnaire.reported_problems_before_leaving ? 'Yes' : 'No'}
                  </p>
                  {accident.driver_questionnaire.reported_problems_reason && (
                    <p className="text-sm text-slate-500 mt-1">{accident.driver_questionnaire.reported_problems_reason}</p>
                  )}
                </div>
                <div className="p-3 border rounded">
                  <Label className="text-xs text-slate-500">Problem occurred previously?</Label>
                  <p className={accident.driver_questionnaire.problem_occurred_previously ? 'text-amber-600' : 'text-slate-600'}>
                    {accident.driver_questionnaire.problem_occurred_previously ? 'Yes' : 'No'}
                  </p>
                  {accident.driver_questionnaire.problem_occurred_previously && accident.driver_questionnaire.problem_occurred_explanation && (
                    <p className="text-sm text-slate-500 mt-1">{accident.driver_questionnaire.problem_occurred_explanation}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Preventability */}
          {accident.preventability && (
            <div className="space-y-2">
              <h3 className="font-semibold text-slate-900 border-b pb-2">Preventability</h3>
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <span className={`inline-flex items-center px-2.5 py-1 rounded font-medium ${
                  accident.preventability === 'preventable' ? 'bg-red-100 text-red-800' :
                  accident.preventability === 'not_preventable' ? 'bg-emerald-100 text-emerald-800' :
                  'bg-slate-200 text-slate-700'
                }`}>
                  {accident.preventability === 'preventable' ? 'Preventable' :
                   accident.preventability === 'not_preventable' ? 'Not Preventable' : 'Undetermined'}
                </span>
                {accident.preventability_reason && (
                  <span className="text-slate-700 flex-1 bg-slate-50 px-3 py-1 rounded">{accident.preventability_reason}</span>
                )}
              </div>
            </div>
          )}

          {/* Notes */}
          {accident.notes && (
            <div className="space-y-2">
              <h3 className="font-semibold text-slate-900 border-b pb-2">Additional Notes</h3>
              <p className="text-sm bg-slate-50 p-3 rounded">{accident.notes}</p>
            </div>
          )}

          {/* Submission Info */}
          <div className="grid grid-cols-2 gap-4 p-3 bg-slate-100 rounded text-sm">
            <div>
              <span className="text-slate-500">Created by:</span>
              <span className="ml-2">{accident.created_by_name}</span>
            </div>
            <div>
              <span className="text-slate-500">Submitted by:</span>
              <span className="ml-2">{accident.submitted_by_name || 'Not submitted'}</span>
            </div>
            {accident.approved_by_name && (
              <div>
                <span className="text-slate-500">Approved by:</span>
                <span className="ml-2">{accident.approved_by_name}</span>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          {canApprove && accident.status === 'pending_approval' && (
            <>
              <Button
                variant="destructive"
                onClick={() => {
                  const reason = prompt('Reason for returning:');
                  if (reason) {
                    onReturn(accident, reason);
                    onClose();
                  }
                }}
              >
                <X className="w-4 h-4 mr-1" />
                Return
              </Button>
              <Button
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={() => {
                  onApprove(accident);
                  onClose();
                }}
              >
                <Check className="w-4 h-4 mr-1" />
                Approve
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Accident Form Dialog
const AccidentFormDialog = ({ open, onClose, accident, onSave, vehicles, trailers, drivers, onRefresh }) => {
  const { user, isAdmin } = useAuth();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [savedAccidentId, setSavedAccidentId] = useState(accident?.id || null);
  const templateInputs = React.useRef({ 'incident-statement': null, 'fatality-report': null });

  const downloadTemplate = async (slug) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/accidents/templates/${slug}`,
        { responseType: 'blob' }
      );
      // Extract original filename from Content-Disposition
      const disposition = res.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^"]+?)"?(?:;|$)/);
      const fallback = slug === 'incident-statement'
        ? 'Incident_Accident_Statement.docx'
        : 'Fatality_Investigation_Report.docx';
      const filename = (match && match[1]) || fallback;
      const contentType = res.headers['content-type'] || res.data.type || 'application/octet-stream';
      const blob = new Blob([res.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Failed to download template');
    }
  };

  const replaceTemplate = async (slug, file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/accidents/templates/${slug}/replace`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('Template replaced');
    } catch (e) {
      toast.error('Failed to replace template');
    }
  };

  const resetTemplate = async (slug) => {
    if (!window.confirm('Remove your custom version and revert to the built-in default template?')) return;
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/accidents/templates/${slug}`);
      toast.success('Reverted to default template');
    } catch (e) {
      toast.error('Failed to reset template');
    }
  };
  
  const initSupportingDocs = () => {
    const docs = {};
    Object.keys(SUPPORTING_DOC_TYPES).forEach(key => {
      docs[key] = { uploaded: false, filename: '', reason: '' };
    });
    return docs;
  };

  const [formData, setFormData] = useState({
    date_of_accident: new Date().toISOString().slice(0, 16),
    truck_id: '',
    trailer_id: '',
    driver_id: '',
    is_fatality: false,
    fatality_count: 0,
    injury_count: 0,
    supporting_docs: initSupportingDocs(),
    conditions: {
      day_night: '',
      weather: '',
      weather_extreme_details: '',
      time_of_event: '',
      day_of_week: '',
    },
    company_rep_present: { answer: null, reason: '' },
    driver_reported_immediately: { answer: null, reason: '', additional_info: '' },
    root_cause_analysis: {
      problem_description: '',
      why_1: '', why_2: '', why_3: '', why_4: '', why_5: '', why_6: '',
      root_cause: '',
      solution: '',
      temporary_countermeasure: '',
      permanent_countermeasure: '',
      monitoring_actions: '',
    },
    driver_questionnaire: {
      did_daily_inspection: null,
      daily_inspection_reason_no: '',
      reported_problems_before_leaving: null,
      reported_problems_reason: '',
      problem_occurred_previously: null,
      problem_occurred_explanation: '',
    },
    preventability: '',
    preventability_reason: '',
    notes: '',
    incident_statement_uploaded: false,
    incident_statement_filename: '',
    fatality_report_uploaded: false,
    fatality_report_filename: '',
  });

  useEffect(() => {
    if (accident) {
      setSavedAccidentId(accident.id);
      // Convert old format to new format if needed
      const existingDocs = accident.supporting_docs || {};
      const convertedDocs = {};
      Object.keys(SUPPORTING_DOC_TYPES).forEach(key => {
        if (typeof existingDocs[key] === 'boolean') {
          convertedDocs[key] = { uploaded: existingDocs[key], filename: '', reason: '' };
        } else {
          convertedDocs[key] = existingDocs[key] || { uploaded: false, filename: '', reason: '' };
        }
      });

      setFormData({
        date_of_accident: accident.date_of_accident?.slice(0, 16) || new Date().toISOString().slice(0, 16),
        truck_id: accident.truck_id || '',
        trailer_id: accident.trailer_id || '',
        driver_id: accident.driver_id || '',
        is_fatality: accident.is_fatality || false,
        fatality_count: accident.fatality_count || 0,
        injury_count: accident.injury_count || 0,
        supporting_docs: convertedDocs,
        conditions: accident.conditions || formData.conditions,
        company_rep_present: accident.company_rep_present || { answer: null, reason: '' },
        driver_reported_immediately: accident.driver_reported_immediately || { answer: null, reason: '', additional_info: '' },
        root_cause_analysis: accident.root_cause_analysis || formData.root_cause_analysis,
        driver_questionnaire: accident.driver_questionnaire || formData.driver_questionnaire,
        preventability: accident.preventability || '',
        preventability_reason: accident.preventability_reason || '',
        notes: accident.notes || '',
        incident_statement_uploaded: accident.incident_statement_uploaded || false,
        incident_statement_filename: accident.incident_statement_filename || '',
        fatality_report_uploaded: accident.fatality_report_uploaded || false,
        fatality_report_filename: accident.fatality_report_filename || '',
      });
    } else {
      setSavedAccidentId(null);
      setFormData({
        date_of_accident: new Date().toISOString().slice(0, 16),
        truck_id: '',
        trailer_id: '',
        driver_id: '',
        is_fatality: false,
        fatality_count: 0,
        injury_count: 0,
        supporting_docs: initSupportingDocs(),
        conditions: {
          day_night: '',
          weather: '',
          weather_extreme_details: '',
          time_of_event: '',
          day_of_week: '',
        },
        company_rep_present: { answer: null, reason: '' },
        driver_reported_immediately: { answer: null, reason: '', additional_info: '' },
        root_cause_analysis: {
          problem_description: '',
          why_1: '', why_2: '', why_3: '', why_4: '', why_5: '', why_6: '',
          root_cause: '',
          solution: '',
          temporary_countermeasure: '',
          permanent_countermeasure: '',
          monitoring_actions: '',
        },
        driver_questionnaire: {
          did_daily_inspection: null,
          daily_inspection_reason_no: '',
          reported_problems_before_leaving: null,
          reported_problems_reason: '',
          problem_occurred_previously: null,
          problem_occurred_explanation: '',
        },
        preventability: '',
        preventability_reason: '',
        notes: '',
        incident_statement_uploaded: false,
        incident_statement_filename: '',
        fatality_report_uploaded: false,
        fatality_report_filename: '',
      });
    }
  }, [accident, open]);

  const updateField = useCallback((section, field, value) => {
    if (section) {
      setFormData(prev => ({
        ...prev,
        [section]: { ...prev[section], [field]: value }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  }, []);

  const handleDocUpload = useCallback((docKey, filename) => {
    setFormData(prev => ({
      ...prev,
      supporting_docs: {
        ...prev.supporting_docs,
        [docKey]: { uploaded: true, filename, reason: '' }
      }
    }));
  }, []);

  const handleDocReasonChange = useCallback((docKey, reason) => {
    setFormData(prev => ({
      ...prev,
      supporting_docs: {
        ...prev.supporting_docs,
        [docKey]: { ...prev.supporting_docs[docKey], reason }
      }
    }));
  }, []);

  const handleMandatoryDocUpload = async (docType, file) => {
    if (!savedAccidentId) {
      toast.error('Please save the accident first before uploading documents');
      return;
    }
    
    setUploading(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      uploadFormData.append('doc_type', docType);
      
      const uploadResp = await axios.post(
        `${API_URL}/api/accidents/${savedAccidentId}/upload-mandatory-document`,
        uploadFormData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      const result = uploadResp.data;
      
      if (docType === 'incident_statement') {
        setFormData(prev => ({ 
          ...prev, 
          incident_statement_uploaded: true,
          incident_statement_filename: result.filename 
        }));
      } else if (docType === 'fatality_report') {
        setFormData(prev => ({ 
          ...prev, 
          fatality_report_uploaded: true,
          fatality_report_filename: result.filename 
        }));
      }
      
      toast.success(`${docType === 'incident_statement' ? 'Incident Statement' : 'Fatality Report'} uploaded successfully`);
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const validateForm = () => {
    if (!formData.truck_id || !formData.driver_id) {
      toast.error('Please select a truck and driver');
      return false;
    }
    
    // Check that each non-uploaded document has a reason
    for (const [key, doc] of Object.entries(formData.supporting_docs)) {
      if (!doc.uploaded && !doc.reason?.trim()) {
        toast.error(`Please provide a reason for not uploading: ${SUPPORTING_DOC_TYPES[key]}`);
        return false;
      }
    }
    
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const result = await onSave(formData);
      if (result?.id) {
        setSavedAccidentId(result.id);
      }
      onClose();
    } catch (error) {
      console.error('Error saving accident:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!formData.truck_id || !formData.driver_id) {
      toast.error('Please select a truck and driver');
      return;
    }

    setLoading(true);
    try {
      const result = await onSave(formData);
      if (result?.id) {
        setSavedAccidentId(result.id);
        toast.success('Draft saved. You can now upload documents.');
      }
    } catch (error) {
      console.error('Error saving draft:', error);
    } finally {
      setLoading(false);
    }
  };

  const trucks = vehicles.filter(v => v.vehicle_type === 'Truck' || v.vehicle_type === 'truck');

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertOctagon className="w-5 h-5 text-red-500" />
            {accident ? 'Edit Accident Report' : 'New Accident Report'}
          </DialogTitle>
          <DialogDescription>
            Complete all sections to document the accident incident. Upload supporting documents or provide reasons if unavailable.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Basic Info */}
          <div className="space-y-4">
            <h3 className="font-semibold text-slate-900 border-b pb-2">Basic Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date of Accident *</Label>
                <Input
                  type="datetime-local"
                  value={formData.date_of_accident}
                  onChange={(e) => updateField(null, 'date_of_accident', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Truck Registration *</Label>
                <Select value={formData.truck_id} onValueChange={(v) => updateField(null, 'truck_id', v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select truck" />
                  </SelectTrigger>
                  <SelectContent>
                    {trucks.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.registration_number} - {t.make} {t.model}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Trailer Registration</Label>
                <Select value={formData.trailer_id || 'none'} onValueChange={(v) => updateField(null, 'trailer_id', v === 'none' ? '' : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select trailer (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Trailer</SelectItem>
                    {trailers.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.registration_number}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Driver Name *</Label>
                <Select value={formData.driver_id} onValueChange={(v) => updateField(null, 'driver_id', v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select driver" />
                  </SelectTrigger>
                  <SelectContent>
                    {drivers.map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        {d.first_name || d.user?.first_name || ''} {d.last_name || d.user?.last_name || ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Fatality/Injury Information */}
          <div className="space-y-4">
            <h3 className="font-semibold text-slate-900 border-b pb-2 flex items-center gap-2">
              <Skull className="w-4 h-4 text-red-600" />
              Fatality/Injury Information
            </h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="is_fatality"
                    checked={formData.is_fatality}
                    onCheckedChange={(v) => updateField(null, 'is_fatality', v)}
                  />
                  <Label htmlFor="is_fatality" className="text-red-600 font-semibold">
                    Fatality Involved
                  </Label>
                </div>
              </div>
              {formData.is_fatality && (
                <div className="space-y-2">
                  <Label>Number of Fatalities</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.fatality_count}
                    onChange={(e) => updateField(null, 'fatality_count', parseInt(e.target.value) || 0)}
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label>Number of Injuries</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.injury_count}
                  onChange={(e) => updateField(null, 'injury_count', parseInt(e.target.value) || 0)}
                />
              </div>
            </div>
          </div>

          {/* Supporting Documents */}
          <div className="space-y-4">
            <div className="flex items-center justify-between border-b pb-2">
              <h3 className="font-semibold text-slate-900">Supporting Documents</h3>
              {!savedAccidentId && (
                <Button variant="outline" size="sm" onClick={handleSaveDraft} disabled={loading}>
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                  Save Draft to Enable Uploads
                </Button>
              )}
            </div>
            {!savedAccidentId && (
              <p className="text-sm text-amber-600 bg-amber-50 p-2 rounded">
                Save the accident as a draft first to enable document uploads.
              </p>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {Object.entries(SUPPORTING_DOC_TYPES).map(([key, label]) => (
                <SupportingDocUpload
                  key={key}
                  docKey={key}
                  label={label}
                  docData={formData.supporting_docs[key]}
                  onUpload={handleDocUpload}
                  onReasonChange={handleDocReasonChange}
                  accidentId={savedAccidentId}
                  disabled={!savedAccidentId}
                />
              ))}
            </div>
          </div>

          {/* Mandatory Documents */}
          <div className="space-y-4">
            <h3 className="font-semibold text-slate-900 border-b pb-2 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Mandatory Documents
            </h3>
            <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 space-y-4">
              {/* Incident Statement - Always Required */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${formData.incident_statement_uploaded ? 'bg-emerald-100' : 'bg-amber-100'}`}>
                      {formData.incident_statement_uploaded ? (
                        <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                      ) : (
                        <AlertTriangle className="w-5 h-5 text-amber-600" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium">Incident/Accident Statement</p>
                      <p className="text-sm text-slate-500">Required for ALL accidents</p>
                    </div>
                  </div>
                  <div className="flex gap-2 flex-wrap items-center">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => downloadTemplate('incident-statement')}
                      className="gap-1"
                      data-testid="download-incident-statement-template"
                    >
                      <Download className="w-4 h-4" />
                      Download Template
                    </Button>
                    {isAdmin && (
                      <>
                        <input
                          type="file"
                          accept=".doc,.docx,.pdf"
                          className="hidden"
                          ref={(el) => (templateInputs.current['incident-statement'] = el)}
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) replaceTemplate('incident-statement', f);
                            e.target.value = '';
                          }}
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-1"
                          onClick={() => templateInputs.current['incident-statement']?.click()}
                          title="Replace template (admin)"
                          data-testid="replace-incident-statement-template"
                        >
                          <Upload className="w-4 h-4" />
                          Replace
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="gap-1 text-red-600 hover:text-red-700"
                          onClick={() => resetTemplate('incident-statement')}
                          title="Revert to default (admin)"
                          data-testid="reset-incident-statement-template"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                    {savedAccidentId && (
                      <label>
                        <input
                          type="file"
                          className="hidden"
                          accept=".doc,.docx,.pdf"
                          onChange={(e) => {
                            if (e.target.files?.[0]) {
                              handleMandatoryDocUpload('incident_statement', e.target.files[0]);
                            }
                          }}
                          disabled={uploading}
                        />
                        <Button
                          variant={formData.incident_statement_uploaded ? "outline" : "default"}
                          size="sm"
                          className="gap-1"
                          disabled={uploading}
                          asChild
                        >
                          <span>
                            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                            {formData.incident_statement_uploaded ? 'Re-upload' : 'Upload'}
                          </span>
                        </Button>
                      </label>
                    )}
                  </div>
                </div>
                {/* Show uploaded file with download link */}
                {formData.incident_statement_uploaded && formData.incident_statement_filename && (
                  <div className="flex items-center gap-2 text-sm text-emerald-600 bg-emerald-50 rounded px-3 py-2 ml-11">
                    <File className="w-4 h-4" />
                    <span className="truncate flex-1">{formData.incident_statement_filename}</span>
                    <a 
                      href={`${API_URL}/api/uploads/accidents/${formData.incident_statement_filename}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline flex items-center gap-1 text-blue-600"
                    >
                      <Download className="w-4 h-4" />
                      View
                    </a>
                  </div>
                )}
              </div>

              {/* Fatality Report - Only if Fatality */}
              {formData.is_fatality && (
                <div className="space-y-2 pt-4 border-t border-amber-300">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${formData.fatality_report_uploaded ? 'bg-emerald-100' : 'bg-red-100'}`}>
                        {formData.fatality_report_uploaded ? (
                          <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                        ) : (
                          <Skull className="w-5 h-5 text-red-600" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-red-700">Fatality Investigation Report</p>
                        <p className="text-sm text-red-600">REQUIRED - Fatality involved</p>
                      </div>
                    </div>
                    <div className="flex gap-2 flex-wrap items-center">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => downloadTemplate('fatality-report')}
                        className="gap-1"
                        data-testid="download-fatality-report-template"
                      >
                        <Download className="w-4 h-4" />
                        Download Template
                      </Button>
                      {isAdmin && (
                        <>
                          <input
                            type="file"
                            accept=".doc,.docx,.pdf"
                            className="hidden"
                            ref={(el) => (templateInputs.current['fatality-report'] = el)}
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              if (f) replaceTemplate('fatality-report', f);
                              e.target.value = '';
                            }}
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-1"
                            onClick={() => templateInputs.current['fatality-report']?.click()}
                            title="Replace template (admin)"
                            data-testid="replace-fatality-report-template"
                          >
                            <Upload className="w-4 h-4" />
                            Replace
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="gap-1 text-red-600 hover:text-red-700"
                            onClick={() => resetTemplate('fatality-report')}
                            title="Revert to default (admin)"
                            data-testid="reset-fatality-report-template"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      {savedAccidentId && (
                        <label>
                          <input
                            type="file"
                            className="hidden"
                            accept=".doc,.docx,.pdf"
                            onChange={(e) => {
                              if (e.target.files?.[0]) {
                                handleMandatoryDocUpload('fatality_report', e.target.files[0]);
                              }
                            }}
                            disabled={uploading}
                          />
                          <Button
                            variant={formData.fatality_report_uploaded ? "outline" : "destructive"}
                            size="sm"
                            className="gap-1"
                            disabled={uploading}
                            asChild
                          >
                            <span>
                              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                              {formData.fatality_report_uploaded ? 'Re-upload' : 'Upload'}
                            </span>
                          </Button>
                        </label>
                      )}
                    </div>
                  </div>
                  {/* Show uploaded file with download link */}
                  {formData.fatality_report_uploaded && formData.fatality_report_filename && (
                    <div className="flex items-center gap-2 text-sm text-emerald-600 bg-emerald-50 rounded px-3 py-2 ml-11">
                      <File className="w-4 h-4" />
                      <span className="truncate flex-1">{formData.fatality_report_filename}</span>
                      <a 
                        href={`${API_URL}/api/uploads/accidents/${formData.fatality_report_filename}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:underline flex items-center gap-1 text-blue-600"
                      >
                        <Download className="w-4 h-4" />
                        View
                      </a>
                    </div>
                  )}
                </div>
              )}

              {!savedAccidentId && (
                <p className="text-sm text-amber-700 italic">
                  Save the accident report first to enable document uploads
                </p>
              )}
            </div>
          </div>

          {/* Conditions of Event */}
          <div className="space-y-4">
            <h3 className="font-semibold text-slate-900 border-b pb-2">Conditions of Event</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Day/Night</Label>
                <Select value={formData.conditions.day_night || ''} onValueChange={(v) => updateField('conditions', 'day_night', v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="day">Day</SelectItem>
                    <SelectItem value="night">Night</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Weather</Label>
                <Select value={formData.conditions.weather || ''} onValueChange={(v) => updateField('conditions', 'weather', v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sun">Sun</SelectItem>
                    <SelectItem value="rain">Rain</SelectItem>
                    <SelectItem value="cloudy">Cloudy</SelectItem>
                    <SelectItem value="fog">Fog</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {formData.conditions.weather === 'rain' && (
                <div className="space-y-2">
                  <Label>If Rain - Extreme Details</Label>
                  <Input
                    value={formData.conditions.weather_extreme_details || ''}
                    onChange={(e) => updateField('conditions', 'weather_extreme_details', e.target.value)}
                    placeholder="Specify extreme conditions"
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label>Time of Event</Label>
                <Input
                  type="time"
                  value={formData.conditions.time_of_event || ''}
                  onChange={(e) => updateField('conditions', 'time_of_event', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Day of Week</Label>
                <Select value={formData.conditions.day_of_week || ''} onValueChange={(v) => updateField('conditions', 'day_of_week', v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => (
                      <SelectItem key={day} value={day}>{day}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Company Representative */}
          <div className="space-y-4">
            <h3 className="font-semibold text-slate-900 border-b pb-2">Company Representative & Reporting</h3>
            <div className="grid gap-4">
              <div className="space-y-2">
                <Label>Was a Company representative at the place of accident?</Label>
                <div className="flex gap-4">
                  <Button
                    type="button"
                    variant={formData.company_rep_present.answer === true ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => updateField('company_rep_present', 'answer', true)}
                  >
                    Yes
                  </Button>
                  <Button
                    type="button"
                    variant={formData.company_rep_present.answer === false ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => updateField('company_rep_present', 'answer', false)}
                  >
                    No
                  </Button>
                </div>
                {formData.company_rep_present.answer === false && (
                  <Input
                    className="mt-2"
                    value={formData.company_rep_present.reason || ''}
                    onChange={(e) => updateField('company_rep_present', 'reason', e.target.value)}
                    placeholder="Reason for no"
                  />
                )}
              </div>
              <div className="space-y-2">
                <Label>Did the driver report the accident to Manager/Supervisor immediately?</Label>
                <div className="flex gap-4">
                  <Button
                    type="button"
                    variant={formData.driver_reported_immediately.answer === true ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => updateField('driver_reported_immediately', 'answer', true)}
                  >
                    Yes
                  </Button>
                  <Button
                    type="button"
                    variant={formData.driver_reported_immediately.answer === false ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => updateField('driver_reported_immediately', 'answer', false)}
                  >
                    No
                  </Button>
                </div>
                {formData.driver_reported_immediately.answer === false && (
                  <div className="mt-2 space-y-2">
                    <Input
                      value={formData.driver_reported_immediately.reason || ''}
                      onChange={(e) => updateField('driver_reported_immediately', 'reason', e.target.value)}
                      placeholder="Reason for no"
                    />
                    <Input
                      value={formData.driver_reported_immediately.additional_info || ''}
                      onChange={(e) => updateField('driver_reported_immediately', 'additional_info', e.target.value)}
                      placeholder="When was it reported?"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Root Cause Analysis */}
          <div className="space-y-4">
            <h3 className="font-semibold text-slate-900 border-b pb-2">Root Cause Analysis</h3>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>What was the problem?</Label>
                <Textarea
                  value={formData.root_cause_analysis.problem_description || ''}
                  onChange={(e) => updateField('root_cause_analysis', 'problem_description', e.target.value)}
                  placeholder="Describe the problem"
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label className="font-semibold">6 WHY's Analysis</Label>
                <div className="grid gap-2">
                  {[1, 2, 3, 4, 5, 6].map(num => (
                    <div key={num} className="flex items-center gap-2">
                      <Label className="w-16 text-sm">WHY {num}:</Label>
                      <Input
                        value={formData.root_cause_analysis[`why_${num}`] || ''}
                        onChange={(e) => updateField('root_cause_analysis', `why_${num}`, e.target.value)}
                        placeholder={`Why ${num}...`}
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Root Cause</Label>
                  <Textarea
                    value={formData.root_cause_analysis.root_cause || ''}
                    onChange={(e) => updateField('root_cause_analysis', 'root_cause', e.target.value)}
                    rows={2}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Solution</Label>
                  <Textarea
                    value={formData.root_cause_analysis.solution || ''}
                    onChange={(e) => updateField('root_cause_analysis', 'solution', e.target.value)}
                    rows={2}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Temporary Countermeasure</Label>
                  <Textarea
                    value={formData.root_cause_analysis.temporary_countermeasure || ''}
                    onChange={(e) => updateField('root_cause_analysis', 'temporary_countermeasure', e.target.value)}
                    rows={2}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Permanent Countermeasure</Label>
                  <Textarea
                    value={formData.root_cause_analysis.permanent_countermeasure || ''}
                    onChange={(e) => updateField('root_cause_analysis', 'permanent_countermeasure', e.target.value)}
                    rows={2}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Monitoring Actions</Label>
                <Textarea
                  value={formData.root_cause_analysis.monitoring_actions || ''}
                  onChange={(e) => updateField('root_cause_analysis', 'monitoring_actions', e.target.value)}
                  rows={2}
                />
              </div>
            </div>
          </div>

          {/* Driver Questionnaire */}
          <div className="space-y-4">
            <h3 className="font-semibold text-slate-900 border-b pb-2">Driver Questionnaire</h3>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Did you do your daily inspection on the truck prior to leaving the yard?</Label>
                <div className="flex gap-4">
                  <Button
                    type="button"
                    variant={formData.driver_questionnaire.did_daily_inspection === true ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => updateField('driver_questionnaire', 'did_daily_inspection', true)}
                  >
                    Yes
                  </Button>
                  <Button
                    type="button"
                    variant={formData.driver_questionnaire.did_daily_inspection === false ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => updateField('driver_questionnaire', 'did_daily_inspection', false)}
                  >
                    No
                  </Button>
                </div>
                {formData.driver_questionnaire.did_daily_inspection === false && (
                  <Input
                    className="mt-2"
                    value={formData.driver_questionnaire.daily_inspection_reason_no || ''}
                    onChange={(e) => updateField('driver_questionnaire', 'daily_inspection_reason_no', e.target.value)}
                    placeholder="Reason for no"
                  />
                )}
              </div>
              <div className="space-y-2">
                <Label>Did you report any problems to your Manager before leaving?</Label>
                <div className="flex gap-4">
                  <Button
                    type="button"
                    variant={formData.driver_questionnaire.reported_problems_before_leaving === true ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => updateField('driver_questionnaire', 'reported_problems_before_leaving', true)}
                  >
                    Yes
                  </Button>
                  <Button
                    type="button"
                    variant={formData.driver_questionnaire.reported_problems_before_leaving === false ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => updateField('driver_questionnaire', 'reported_problems_before_leaving', false)}
                  >
                    No
                  </Button>
                </div>
                <Input
                  className="mt-2"
                  value={formData.driver_questionnaire.reported_problems_reason || ''}
                  onChange={(e) => updateField('driver_questionnaire', 'reported_problems_reason', e.target.value)}
                  placeholder={formData.driver_questionnaire.reported_problems_before_leaving ? "What was reported?" : "Reason for no"}
                />
              </div>
              <div className="space-y-2">
                <Label>Has this problem occurred previously on your truck?</Label>
                <div className="flex gap-4">
                  <Button
                    type="button"
                    variant={formData.driver_questionnaire.problem_occurred_previously === true ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => updateField('driver_questionnaire', 'problem_occurred_previously', true)}
                  >
                    Yes
                  </Button>
                  <Button
                    type="button"
                    variant={formData.driver_questionnaire.problem_occurred_previously === false ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => updateField('driver_questionnaire', 'problem_occurred_previously', false)}
                  >
                    No
                  </Button>
                </div>
                {formData.driver_questionnaire.problem_occurred_previously === true && (
                  <Textarea
                    className="mt-2"
                    value={formData.driver_questionnaire.problem_occurred_explanation || ''}
                    onChange={(e) => updateField('driver_questionnaire', 'problem_occurred_explanation', e.target.value)}
                    placeholder="Explain previous occurrence"
                    rows={2}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Preventability */}
          <div className="space-y-2 p-4 bg-slate-50 rounded-lg border">
            <Label className="text-sm font-semibold text-slate-800">Preventability Assessment</Label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              {[
                { value: 'preventable', label: 'Preventable', color: 'text-red-700 border-red-300 bg-red-50' },
                { value: 'not_preventable', label: 'Not Preventable', color: 'text-emerald-700 border-emerald-300 bg-emerald-50' },
                { value: 'undetermined', label: 'Undetermined', color: 'text-slate-700 border-slate-300 bg-white' },
              ].map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => updateField(null, 'preventability', opt.value)}
                  className={`px-3 py-2 rounded border text-sm font-medium transition-all ${formData.preventability === opt.value ? `${opt.color} ring-2 ring-offset-1 ring-slate-400` : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
                  data-testid={`accident-preventability-${opt.value}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <Textarea
              value={formData.preventability_reason || ''}
              onChange={(e) => updateField(null, 'preventability_reason', e.target.value)}
              placeholder="Reason / justification for the preventability decision..."
              rows={2}
              data-testid="accident-preventability-reason"
            />
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label>Additional Notes</Label>
            <Textarea
              value={formData.notes || ''}
              onChange={(e) => updateField(null, 'notes', e.target.value)}
              placeholder="Any additional information..."
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={loading}>
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {accident ? 'Update' : 'Create'} Accident
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main Page Component
export const AccidentsPage = () => {
  const { user, isAdmin, isSupervisor, isManagement } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  
  const [accidents, setAccidents] = useState([]);
  const [pendingApproval, setPendingApproval] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [trailers, setTrailers] = useState([]);
  const [drivers, setDrivers] = useState([]);
  
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [selectedAccident, setSelectedAccident] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [accidentsRes, pendingRes, statsRes, vehiclesRes, trailersRes, driversRes] = await Promise.all([
        accidentsAPI.list(),
        accidentsAPI.getPendingApproval(),
        accidentsAPI.getStatistics(),
        vehiclesAPI.list(),
        trailersAPI.list(),
        driversAPI.list(),
      ]);
      
      setAccidents(accidentsRes.data || []);
      setPendingApproval(pendingRes.data || []);
      setStatistics(statsRes.data || {});
      setVehicles(vehiclesRes.data || []);
      setTrailers(trailersRes.data || []);
      setDrivers(driversRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load accidents data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreateAccident = async (data) => {
    try {
      let result;
      if (selectedAccident) {
        result = await accidentsAPI.update(selectedAccident.id, data);
        toast.success('Accident updated successfully');
      } else {
        result = await accidentsAPI.create(data, user.id, `${user.first_name} ${user.last_name}`);
        toast.success('Accident created successfully');
      }
      setSelectedAccident(null);
      fetchData();
      return result.data;
    } catch (error) {
      const detail = error.response?.data?.detail;
      const errorMsg = typeof detail === 'string' 
        ? detail 
        : Array.isArray(detail) 
          ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ')
          : 'Failed to save accident';
      toast.error(errorMsg);
      throw error;
    }
  };

  const handleSubmit = async (accident) => {
    try {
      await accidentsAPI.submit(accident.id, user.id, `${user.first_name} ${user.last_name}`);
      toast.success('Accident submitted for approval');
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const errorMsg = typeof detail === 'string' ? detail : 'Failed to submit accident';
      toast.error(errorMsg);
    }
  };

  const handleApprove = async (accident) => {
    try {
      await accidentsAPI.approve(accident.id, user.id, `${user.first_name} ${user.last_name}`);
      toast.success('Accident approved');
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const errorMsg = typeof detail === 'string' ? detail : 'Failed to approve accident';
      toast.error(errorMsg);
    }
  };

  const handleReturn = async (accident, reason) => {
    try {
      await accidentsAPI.return(accident.id, reason, user.id, `${user.first_name} ${user.last_name}`);
      toast.success('Accident returned for corrections');
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const errorMsg = typeof detail === 'string' ? detail : 'Failed to return accident';
      toast.error(errorMsg);
    }
  };

  const handleDelete = async (accident) => {
    if (!window.confirm('Are you sure you want to delete this draft?')) return;
    try {
      await accidentsAPI.delete(accident.id);
      toast.success('Accident deleted');
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const errorMsg = typeof detail === 'string' ? detail : 'Failed to delete accident';
      toast.error(errorMsg);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const canApprove = isManagement || isAdmin;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="accidents-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 
            className="text-3xl font-bold text-slate-900"
            style={{ fontFamily: 'Barlow Condensed' }}
          >
            ACCIDENTS
          </h1>
          <p className="text-slate-500 mt-1">
            Manage and track vehicle accident reports
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            variant="outline"
            onClick={() => accidentsAPI.exportExcel()}
            className="gap-2"
          >
            <Download className="w-4 h-4" />
            Export
          </Button>
          <Button 
            onClick={() => {
              setSelectedAccident(null);
              setFormDialogOpen(true);
            }}
            className="gap-2 bg-red-600 hover:bg-red-700"
          >
            <Plus className="w-4 h-4" />
            New Accident
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      {statistics && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="border-l-4 border-l-red-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Open</p>
                  <p className="text-2xl font-bold">{statistics.open_count}</p>
                </div>
                <AlertOctagon className="w-8 h-8 text-red-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Pending Approval</p>
                  <p className="text-2xl font-bold">{statistics.pending_approval_count}</p>
                </div>
                <Clock className="w-8 h-8 text-blue-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-emerald-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Closed</p>
                  <p className="text-2xl font-bold">{statistics.closed_count}</p>
                </div>
                <CheckCircle2 className="w-8 h-8 text-emerald-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-black">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Fatalities</p>
                  <p className="text-2xl font-bold text-red-600">{statistics.fatality_count}</p>
                </div>
                <Skull className="w-8 h-8 text-slate-800 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-slate-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">This Month</p>
                  <p className="text-2xl font-bold">{statistics.month_count}</p>
                </div>
                <Calendar className="w-8 h-8 text-slate-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all">All Accidents ({accidents.length})</TabsTrigger>
          {canApprove && (
            <TabsTrigger value="pending">
              Pending Approval ({pendingApproval.length})
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="all" className="mt-4">
          {accidents.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <AlertOctagon className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700">No accidents recorded</h3>
                <p className="text-slate-500 mt-1 mb-4">Create a new accident report to get started</p>
                <Button onClick={() => setFormDialogOpen(true)} className="bg-red-600 hover:bg-red-700">
                  <Plus className="w-4 h-4 mr-2" />
                  New Accident
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {accidents.map((accident) => (
                <Card 
                  key={accident.id}
                  className={`hover:shadow-md transition-all cursor-pointer ${accident.is_fatality ? 'border-l-4 border-l-black' : ''}`}
                  data-testid={`accident-card-${accident.id}`}
                  onClick={() => {
                    setSelectedAccident(accident);
                    setDetailDialogOpen(true);
                  }}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${accident.is_fatality ? 'bg-black' : 'bg-red-100'}`}>
                          {accident.is_fatality ? (
                            <Skull className="w-5 h-5 text-white" />
                          ) : (
                            <AlertOctagon className="w-5 h-5 text-red-600" />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-bold">{accident.truck_registration}</span>
                            {accident.trailer_registration && (
                              <>
                                <ChevronRight className="w-4 h-4 text-slate-400" />
                                <span className="font-mono text-slate-600">{accident.trailer_registration}</span>
                              </>
                            )}
                            {accident.is_fatality && (
                              <Badge className="bg-black text-white">FATALITY</Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-sm text-slate-500">
                            <span>
                              <Calendar className="w-3 h-3 inline mr-1" />
                              {accident.date_of_accident?.slice(0, 10)}
                            </span>
                            <span>
                              <User className="w-3 h-3 inline mr-1" />
                              {accident.driver_name}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={getStatusBadge(accident.status)}>
                          {getStatusLabel(accident.status)}
                        </Badge>
                        {accident.status === 'draft' && (
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedAccident(accident);
                                setFormDialogOpen(true);
                              }}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSubmit(accident);
                              }}
                            >
                              <Send className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(accident);
                              }}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                        {accident.status === 'pending_approval' && canApprove && (
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-emerald-600"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleApprove(accident);
                              }}
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600"
                              onClick={(e) => {
                                e.stopPropagation();
                                const reason = prompt('Reason for returning:');
                                if (reason) handleReturn(accident, reason);
                              }}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="pending" className="mt-4">
          {pendingApproval.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <CheckCircle2 className="w-12 h-12 text-emerald-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700">No pending approvals</h3>
                <p className="text-slate-500 mt-1">All accident reports have been reviewed</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {pendingApproval.map((accident) => (
                <Card key={accident.id} className={`border-l-4 ${accident.is_fatality ? 'border-l-black' : 'border-l-red-500'}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-mono font-bold">{accident.truck_registration}</span>
                          <span className="text-sm text-slate-500">
                            - {accident.date_of_accident?.slice(0, 10)}
                          </span>
                          {accident.is_fatality && (
                            <Badge className="bg-black text-white">FATALITY</Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-600">
                          Submitted by: {accident.submitted_by_name}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedAccident(accident);
                            setDetailDialogOpen(true);
                          }}
                          data-testid={`review-btn-${accident.id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Review
                        </Button>
                        <Button
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => handleApprove(accident)}
                        >
                          <Check className="w-4 h-4 mr-1" />
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => {
                            const reason = prompt('Reason for returning:');
                            if (reason) handleReturn(accident, reason);
                          }}
                        >
                          <X className="w-4 h-4 mr-1" />
                          Return
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Form Dialog */}
      <AccidentFormDialog
        open={formDialogOpen}
        onClose={() => {
          setFormDialogOpen(false);
          setSelectedAccident(null);
        }}
        accident={selectedAccident}
        onSave={handleCreateAccident}
        vehicles={vehicles}
        trailers={trailers}
        drivers={drivers}
        onRefresh={fetchData}
      />

      {/* Detail Dialog */}
      <AccidentDetailDialog
        open={detailDialogOpen}
        onClose={() => {
          setDetailDialogOpen(false);
          setSelectedAccident(null);
        }}
        accident={selectedAccident}
        onApprove={handleApprove}
        onReturn={handleReturn}
        canApprove={canApprove}
      />
    </div>
  );
};

export default AccidentsPage;
